import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { FadeIn } from '@/components/motion/FadeIn';
import { MarkdownRenderer } from '@/components/marketing/MarkdownRenderer';
import { useLegalPage } from '@/lib/cms';
import { formatDate } from '@/lib/utils';

const SLUGS = ['privacy', 'terms', 'cookies'] as const;
type Slug = (typeof SLUGS)[number];

function isSlug(value: string | undefined): value is Slug {
  return !!value && (SLUGS as readonly string[]).includes(value);
}

const FALLBACK: Record<Slug, { title: string; body_md: string }> = {
  privacy: {
    title: 'Privacy policy',
    body_md: `# Privacy policy\n\nThis is a placeholder privacy policy. Edit the full version from the admin portal.\n\n## What we collect\n\nWe collect only the information you give us via forms on this site (contact form, job applications).\n\n## How we use it\n\nWe use your information only to respond to your enquiry. We don't sell or share your data.\n\n## Your rights\n\nYou can request access to or deletion of your data at any time by emailing contact@vonlof.co.\n\n## Contact\n\nQuestions about privacy? Email contact@vonlof.co.`,
  },
  terms: {
    title: 'Terms of service',
    body_md: `# Terms of service\n\nPlaceholder terms of service. Edit from the admin portal.\n\n## Acceptance\n\nBy using this site you agree to these terms.\n\n## Intellectual property\n\nAll content on this site is the property of Vonlof unless otherwise noted.\n\n## Limitation of liability\n\nThis site is provided as-is. We don't warrant that it will be uninterrupted or error-free.\n\n## Governing law\n\nThese terms are governed by the laws of Ghana.`,
  },
  cookies: {
    title: 'Cookie policy',
    body_md: `# Cookie policy\n\nPlaceholder cookie policy. Edit from the admin portal.\n\n## What we use\n\nWe use a single strictly-necessary cookie to remember your theme preference and your cookie consent.\n\n## Analytics\n\nWe use Cloudflare Web Analytics, which is cookie-less.\n\n## Your choices\n\nYou can decline non-essential cookies via the cookie banner.`,
  },
};

export function LegalPage() {
  const { slug } = useParams<{ slug: string }>();
  if (!isSlug(slug)) {
    return (
      <>
        <Meta title="Not found" path={`/legal/${slug ?? ''}`} />
        <section className="flex min-h-[70vh] items-center justify-center pt-20">
          <div className="container-narrow text-center">
            <h1 className="display-serif text-display-l text-fg">Page not found.</h1>
            <div className="mt-6">
              <Button asChild>
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  const { data: page, isLoading } = useLegalPage(slug);
  const fallback = FALLBACK[slug];
  const title = page?.title ?? fallback.title;
  const body = page?.body_md ?? fallback.body_md;

  return (
    <>
      <Meta title={title} path={`/legal/${slug}`} />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <section className="container-narrow">
          <FadeIn>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-fg-muted transition-colors hover:text-gold-500"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to home
            </Link>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h1 className="mt-6 display-serif text-display-l text-fg">{title}</h1>
          </FadeIn>
          {page?.effective_at && (
            <FadeIn delay={0.08}>
              <p className="mt-2 text-sm text-fg-subtle">Last updated {formatDate(page.effective_at)}</p>
            </FadeIn>
          )}
          <FadeIn delay={0.1}>
            {isLoading ? (
              <div className="mt-8 space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            ) : (
              <div className="prose mt-8">
                <MarkdownRenderer content={body} />
              </div>
            )}
          </FadeIn>
        </section>
      </main>
    </>
  );
}